// Lead Generation System — the EXTERNAL Lead Generator console HTTP client.
//
// Mirrors the C# records in src/PhoenixCapital.Core/DTOs/LeadGen/*.cs and the
// routes on PortalLeadGeneratorController (route base api/portal/lead-generator).
// Enums are STRING unions because the API serializes every enum with
// JsonStringEnumConverter (Program.cs). Every call rides the portal cookie
// (portalApi → credentials:"include"); the controller hard-scopes to the
// authenticated generator, so nothing here supplies a generator id.

import { portalApi } from "./client";
import type { DocumentDownloadDto } from "./types";

// ── Enums (string unions matching the C# enum member names) ──────────────────

export type PreleadStatus =
  | "Draft"
  | "MessageSent"
  | "ResponseReceived"
  | "NdaRequested"
  | "NdaSubmitted"
  | "CimRequested"
  | "CimReceived"
  | "Interested"
  | "Escalated"
  | "Contacted"
  | "BadFit"
  | "Duplicate"
  | "NoResponse"
  | "Closed";

export type PreleadSource =
  | "BizBuySell"
  | "DealStream"
  | "LoopNet"
  | "BrokerWebsite"
  | "LinkedIn"
  | "ColdOutreach"
  | "Referral"
  | "Marketplace"
  | "Other";

export type PreleadNdaStatus = "NotRequested" | "Requested" | "Completed";
export type PreleadCimStatus = "NotRequested" | "Requested" | "Received";
export type PreleadInterestLevel = "Unknown" | "Cold" | "Warm" | "Hot";

// The four INDEPENDENT milestone tracks (advanceable in any order). The
// headline PreleadStatus is derived server-side as the furthest milestone.
export type PreleadOutreachStatus = "None" | "MessageSent" | "ResponseReceived";
export type PreleadInterestStatus = "None" | "Interested";

export type PreleadActivityType =
  | "PreleadCreated"
  | "StatusChanged"
  | "NoteAdded"
  | "MessageSent"
  | "ResponseReceived"
  | "NdaRequested"
  | "NdaCompleted"
  | "CimRequested"
  | "CimReceived"
  | "MarkedInterested"
  | "EscalatedToLead"
  | "FileUploaded"
  | "AiExtracted"
  | "Reassigned"
  | "Archived";

export type LeadGenEarningState = "Estimated" | "PendingReview" | "Approved" | "Adjusted";

// ── DTOs ─────────────────────────────────────────────────────────────────────

export interface PagedResult<T> {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface PreleadSummaryDto {
  id: string;
  leadGeneratorId: string;
  businessName: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  phone: string | null;
  email: string | null;
  city: string | null;
  state: string | null;
  industry: string | null;
  askingPrice: number | null;
  cashFlow: number | null;
  source: PreleadSource;
  status: PreleadStatus;
  interestLevel: PreleadInterestLevel;
  ndaStatus: PreleadNdaStatus;
  cimStatus: PreleadCimStatus;
  lastContactDate: string | null;
  nextFollowUpDate: string | null;
  escalatedToLeadId: string | null;
  isArchived: boolean;
  createdAt: string;
  // Four-track sub-states (the headline `status` is derived from these).
  outreachStatus: PreleadOutreachStatus;
  interestStatus: PreleadInterestStatus;
  // Confirmed funnel outcomes (green chips) fed back from the escalated Lead.
  confirmedContactedAtUtc: string | null;
  confirmedInterestedAtUtc: string | null;
  // Running per-prelead earned total = SUM of every credited state. Display-only.
  totalEarned: number;
  // Fallback-label inputs when businessName is blank (empty/partial captures are
  // normal now): the listing URL + a short first-line snippet of the raw paste.
  listingUrl: string | null;
  rawTextSnippet: string | null;
}

export interface PreleadDetailDto {
  id: string;
  leadGeneratorId: string;
  businessName: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  title: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  industry: string | null;
  askingPrice: number | null;
  cashFlow: number | null;
  listingUrl: string | null;
  brokerName: string | null;
  brokerCompany: string | null;
  brokerEmail: string | null;
  brokerPhone: string | null;
  source: PreleadSource;
  sourceDetail: string | null;
  status: PreleadStatus;
  interestLevel: PreleadInterestLevel;
  notes: string | null;
  lastContactDate: string | null;
  nextFollowUpDate: string | null;
  // --- Outreach track ---
  outreachStatus: PreleadOutreachStatus;
  messageSentAtUtc: string | null;
  responseReceivedAtUtc: string | null;
  // --- Interest track ---
  interestStatus: PreleadInterestStatus;
  interestedAtUtc: string | null;
  ndaStatus: PreleadNdaStatus;
  ndaRequestedAtUtc: string | null;
  ndaCompletedAtUtc: string | null;
  ndaDocumentId: string | null;
  cimStatus: PreleadCimStatus;
  cimRequestedAtUtc: string | null;
  cimReceivedAtUtc: string | null;
  cimDocumentId: string | null;
  rawPastedText: string | null;
  capturedLinksJson: string | null;
  aiSummary: string | null;
  aiFitFlagsJson: string | null;
  aiModel: string | null;
  aiExtractedAtUtc: string | null;
  escalatedToLeadId: string | null;
  escalatedAtUtc: string | null;
  isArchived: boolean;
  createdAt: string;
  modifiedAt: string | null;
  // Confirmed funnel outcomes (green chips) fed back from the escalated Lead.
  confirmedContactedAtUtc: string | null;
  confirmedInterestedAtUtc: string | null;
}

/** One credited ledger row for a prelead. `amount` is the row's CreditedAmount
 *  (so the line items sum to {@link PreleadEarningsDto.total}); `state` is the
 *  earning state string (Estimated / PendingReview / Approved / Adjusted). */
export interface PreleadEarningLineDto {
  kpiCode: string;
  label: string;
  amount: number;
  state: string;
  approvedAtUtc: string | null;
}

/** Per-prelead compensation breakdown: the running earned total + one line per
 *  credited ledger row. Standalone / display-only — nothing here moves money. */
export interface PreleadEarningsDto {
  total: number;
  lineItems: PreleadEarningLineDto[];
}

export interface PreleadActivityDto {
  id: string;
  preleadId: string;
  activityType: PreleadActivityType;
  description: string;
  metadataJson: string | null;
  performedByUserId: string;
  timestamp: string;
}

export interface PreleadStatsDto {
  total: number;
  byStatus: Partial<Record<PreleadStatus, number>>;
}

/** One append-only note on a Prelead's thread (oldest-first). `source` is
 *  "AI" (a distilled paste summary) or "User" (typed). */
export interface PreleadNoteDto {
  id: string;
  preleadId: string;
  body: string;
  source: "AI" | "User";
  createdAtUtc: string;
  createdBy: string | null;
}

/** A field the re-paste found a DIFFERENT non-empty value for. `applied` is
 *  true only when the caller confirmed the overwrite. */
export interface PreleadFieldDiscrepancyDto {
  field: string;
  existingValue: string | null;
  pastedValue: string | null;
  applied: boolean;
}

/** Result of a re-paste: the updated Prelead, the field discrepancies (for the
 *  "overwrite these?" prompt), and the id of the AI note just appended. */
export interface PreleadRePasteResultDto {
  prelead: PreleadDetailDto;
  discrepancies: PreleadFieldDiscrepancyDto[];
  noteId: string;
}

export interface PreleadExtractionResultDto {
  success: boolean;
  error: string | null;
  businessName: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  industry: string | null;
  askingPrice: number | null;
  cashFlow: number | null;
  listingUrl: string | null;
  brokerName: string | null;
  brokerEmail: string | null;
  brokerPhone: string | null;
  summary: string | null;
  fitFlags: string[];
  modelUsed: string;
}

export interface LeadGenBucketSummaryDto {
  bucket: string;
  count: number;
  earned: number;
  credited: number;
  capped: number;
  weeklyCap: number | null;
  weeklyCapRemaining: number | null;
  monthlyCap: number | null;
  monthlyCapRemaining: number | null;
}

export interface LeadGenEarningsSummaryDto {
  leadGeneratorId: string;
  weekOf: string;
  buckets: LeadGenBucketSummaryDto[];
  totalCount: number;
  totalEarned: number;
  totalCredited: number;
  totalCapped: number;
  weeklyTotalCap: number | null;
  weeklyTotalCapRemaining: number | null;
  monthlyTotalCap: number | null;
  monthlyTotalCapRemaining: number | null;
  estimatedCredited: number;
  pendingReviewCredited: number;
  approvedCredited: number;
}

export interface LeadGenFeedbackDto {
  id: string;
  leadGeneratorId: string;
  authorUserId: string;
  body: string;
  category: string | null;
  weekOf: string | null;
  acknowledgedAtUtc: string | null;
  createdAt: string;
}

export interface EscalatePreleadResultDto {
  leadId: string;
}

// ── Request shapes ───────────────────────────────────────────────────────────

export interface PreleadListQuery {
  page?: number;
  pageSize?: number;
  /** Free-text search over business / contact / email / phone / listing URL. */
  q?: string;
  status?: PreleadStatus;
  /** Multi-select milestone filter (AND semantics): a prelead matches only when
   *  it has reached EVERY selected token (message_sent, response_received,
   *  nda_complete, cim_received, interested, escalated, confirmed_contacted,
   *  confirmed_interested). Sent as repeated `?statuses=` query params. */
  statuses?: string[];
  source?: PreleadSource;
  state?: string;
  interestLevel?: PreleadInterestLevel;
  archived?: boolean;
  hasFollowUp?: boolean;
  /** Which timestamp the from/to range filters on. */
  dateField?: "created" | "updated";
  /** ISO lower/upper bounds on the chosen date field. */
  from?: string;
  to?: string;
}

export interface CreatePreleadRequest {
  businessName?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  title?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  industry?: string | null;
  askingPrice?: number | null;
  cashFlow?: number | null;
  listingUrl?: string | null;
  brokerName?: string | null;
  brokerCompany?: string | null;
  brokerEmail?: string | null;
  brokerPhone?: string | null;
  source?: PreleadSource;
  sourceDetail?: string | null;
  notes?: string | null;
}

/** Inline field edits from the detail view. Null = leave unchanged; the
 *  caller sends the full shape (FIND-12 discipline). */
export interface UpdatePreleadRequest {
  businessName?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  title?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  industry?: string | null;
  askingPrice?: number | null;
  cashFlow?: number | null;
  listingUrl?: string | null;
  brokerName?: string | null;
  brokerCompany?: string | null;
  brokerEmail?: string | null;
  brokerPhone?: string | null;
  source?: PreleadSource | null;
  sourceDetail?: string | null;
  interestLevel?: PreleadInterestLevel | null;
  notes?: string | null;
  nextFollowUpDate?: string | null;
}

export interface PortalPasteExtractRequest {
  rawText: string;
  capturedLinksJson?: string | null;
  source?: PreleadSource;
  sourceDetail?: string | null;
}

/** Body for the detail-view "Paste update": re-run AI extraction over new
 *  pasted text → append a NEW AI note + merge structured fields (empty filled
 *  silently; a differing non-empty field overwritten only when
 *  `confirmOverwrite`, else reported as a discrepancy). */
export interface PortalRePasteRequest {
  rawText: string;
  capturedLinksJson?: string | null;
  confirmOverwrite?: boolean;
}

/** One captured hyperlink from a rich (HTML) paste. Serialized into
 *  {@link PortalPasteExtractRequest.capturedLinksJson}. The stored JSON now
 *  holds the AI-filtered, listing-pertinent links; each entry may carry an
 *  optional AI-classified `type` (e.g. "listing", "broker"). */
export interface CapturedLink {
  label: string;
  href: string;
  type?: string;
}

/** One general file attached to a prelead (unified NDA / CIM / other files).
 *  Mirrors the C# PreleadAttachmentDto. */
export interface PreleadAttachmentDto {
  docId: string;
  fileName: string;
  extension: string | null;
  sizeBytes: number;
  category: string;
  tags: string | null;
  createdAt: string;
}

// ── Client ───────────────────────────────────────────────────────────────────

const BASE = "/lead-generator";

/** Serialize a prelead list query, emitting `statuses` as REPEATED params
 *  (the shared portal client's scalar Query map can't express an array — ASP.NET
 *  binds `string[]? Statuses` from `?statuses=x&statuses=y`). Blank/undefined
 *  values are omitted. Returns the full `path?query` string. */
function buildPreleadListPath(query: PreleadListQuery): string {
  const params = new URLSearchParams();
  const put = (k: string, v: string | number | boolean | null | undefined) => {
    if (v === undefined || v === null || v === "") return;
    params.append(k, String(v));
  };
  put("page", query.page);
  put("pageSize", query.pageSize);
  put("q", query.q);
  put("status", query.status);
  put("source", query.source);
  put("state", query.state);
  put("interestLevel", query.interestLevel);
  put("archived", query.archived);
  put("hasFollowUp", query.hasFollowUp);
  put("dateField", query.dateField);
  put("from", query.from);
  put("to", query.to);
  for (const token of query.statuses ?? []) {
    if (token) params.append("statuses", token);
  }
  const qs = params.toString();
  return qs ? `${BASE}/preleads?${qs}` : `${BASE}/preleads`;
}

export const leadGeneratorApi = {
  // reads
  listPreleads: (query: PreleadListQuery = {}) =>
    portalApi.get<PagedResult<PreleadSummaryDto>>(buildPreleadListPath(query)),
  getStats: (query: PreleadListQuery = {}) =>
    portalApi.get<PreleadStatsDto>(`${BASE}/preleads/stats`, {
      q: query.q,
      source: query.source,
      state: query.state,
      interestLevel: query.interestLevel,
      archived: query.archived,
    }),
  getPrelead: (id: string) => portalApi.get<PreleadDetailDto>(`${BASE}/preleads/${id}`),
  /** Standalone display-only compensation breakdown for one of the generator's
   *  own preleads (running total + per-credit line items). Scoped server-side. */
  preleadEarnings: (id: string) =>
    portalApi.get<PreleadEarningsDto>(`${BASE}/preleads/${id}/earnings`),
  getActivity: (id: string) =>
    portalApi.get<PreleadActivityDto[]>(`${BASE}/preleads/${id}/activity`),

  // notes thread (oldest-first)
  listNotes: (id: string) =>
    portalApi.get<PreleadNoteDto[]>(`${BASE}/preleads/${id}/notes`),
  addNote: (id: string, body: string) =>
    portalApi.post<PreleadNoteDto>(`${BASE}/preleads/${id}/notes`, { body }),

  // inline field edits (detail view)
  update: (id: string, body: UpdatePreleadRequest) =>
    portalApi.patch<PreleadDetailDto>(`${BASE}/preleads/${id}`, body),

  // re-paste an update over an existing prelead → new AI note + field merge
  rePaste: (id: string, body: PortalRePasteRequest) =>
    portalApi.post<PreleadRePasteResultDto>(`${BASE}/preleads/${id}/re-paste`, body),

  // create
  createManual: (body: CreatePreleadRequest) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads`, body),
  /** Paste → AI extract → create/top-up. Rejects with a PortalApiError whose
   *  status is 422 when the extraction pipeline fails (the console falls back
   *  to the manual form). */
  extract: (body: PortalPasteExtractRequest) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/extract`, body),

  // one-click lifecycle — each returns the updated PreleadDetailDto
  messageSent: (id: string) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/message-sent`),
  responseReceived: (id: string) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/response-received`),
  requestNda: (id: string) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/request-nda`),
  completeNda: (id: string) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/complete-nda`),
  requestCim: (id: string) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/request-cim`),
  receiveCim: (id: string) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/receive-cim`),
  markInterested: (id: string) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/mark-interested`),
  badFit: (id: string) => portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/bad-fit`),
  duplicate: (id: string) => portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/duplicate`),
  noResponse: (id: string) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/no-response`),
  close: (id: string) => portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/close`),

  // track reverts (overflow menu) — step ONE stage back; a reverted paying
  // milestone writes an offsetting KPI reversal server-side. Each returns the
  // updated PreleadDetailDto.
  revertMessageSent: (id: string) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/revert-message-sent`),
  revertResponseReceived: (id: string) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/revert-response-received`),
  revertNdaRequested: (id: string) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/revert-nda-requested`),
  revertNdaCompleted: (id: string) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/revert-nda-completed`),
  revertCimRequested: (id: string) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/revert-cim-requested`),
  revertCimReceived: (id: string) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/revert-cim-received`),
  revertInterested: (id: string) =>
    portalApi.post<PreleadDetailDto>(`${BASE}/preleads/${id}/revert-interested`),

  // NDA / CIM signed-file upload (multipart; the controller binds IFormFile "file")
  uploadNda: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return portalApi.upload<DocumentDownloadDto>(`${BASE}/preleads/${id}/nda-file`, form);
  },
  uploadCim: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return portalApi.upload<DocumentDownloadDto>(`${BASE}/preleads/${id}/cim-file`, form);
  },

  // general prelead attachments — unified downloadable file list (NDA/CIM/other)
  listAttachments: (id: string) =>
    portalApi.get<PreleadAttachmentDto[]>(`${BASE}/preleads/${id}/attachments`),
  uploadAttachment: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return portalApi.upload<DocumentDownloadDto>(`${BASE}/preleads/${id}/attachments`, form);
  },
  attachmentDownload: (id: string, docId: string) =>
    portalApi.get<DocumentDownloadDto>(`${BASE}/preleads/${id}/attachments/${docId}`),

  // escalation → returns the new lead id
  escalate: (id: string) =>
    portalApi.post<EscalatePreleadResultDto>(`${BASE}/preleads/${id}/escalate`),

  // own earnings + feedback
  earnings: (weekOf?: string | null) =>
    portalApi.get<LeadGenEarningsSummaryDto>(`${BASE}/earnings`, weekOf ? { weekOf } : undefined),
  feedback: () => portalApi.get<LeadGenFeedbackDto[]>(`${BASE}/feedback`),
};
